/**
 * Som de "paciente chamado" — duas notas curtas (G5 → C6) via WebAudio,
 * sem asset de áudio no bundle. Mais presente que o ping do chat
 * ([[chatAlert]]) porque sinaliza uma ação que exige reação: alguém
 * precisa notar que o paciente foi chamado.
 *
 * Usado tanto pelo painel de TV (CallPanelPage) quanto pela Agenda (aviso
 * pra recepção quando o médico chama um paciente) — mesma identidade
 * sonora nos dois lugares.
 *
 * Reusa um único AudioContext e se desbloqueia no primeiro gesto do
 * usuário (mesmo padrão do chatAlert), porque browsers bloqueiam áudio
 * até haver interação com a página. Falha sempre em silêncio — som nunca
 * derruba a UI. A preferência de mute fica a cargo de quem chama.
 */

let sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (sharedCtx) return sharedCtx;
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return null;
    sharedCtx = new AudioCtx();
    return sharedCtx;
  } catch {
    return null;
  }
}

// Desbloqueia o áudio no primeiro click/keydown/touch da página — sem
// isso o primeiro chime falha silenciosamente até o usuário interagir.
if (typeof document !== 'undefined') {
  let unlocked = false;
  const unlock = () => {
    if (unlocked) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => { unlocked = true; }).catch(() => {});
    } else {
      unlocked = true;
    }
  };
  document.addEventListener('click', unlock, { capture: true });
  document.addEventListener('keydown', unlock, { capture: true });
  document.addEventListener('touchstart', unlock, { capture: true, passive: true });
}

/**
 * Toca o "ding" de chamada (G5 → C6, ~0.6s). Falha em silêncio se o
 * navegador ainda não autorizou áudio (gesto do usuário necessário).
 */
export function playCallChime(): void {
  const ctx = getCtx();
  if (!ctx) return;
  // Alguns browsers suspendem o ctx até um gesto; tenta retomar.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  try {
    const now = ctx.currentTime;
    const notes: Array<{ freq: number; start: number; dur: number }> = [
      { freq: 783.99, start: 0, dur: 0.18 },    // G5
      { freq: 1046.5, start: 0.18, dur: 0.45 }, // C6
    ];
    for (const { freq, start, dur } of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.35, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    }
  } catch {
    // Áudio indisponível/bloqueado — silencioso, não é fatal.
  }
}

/**
 * Bipes do ditado aberto sem clique (Alt+V / pedal; spec 011): o médico está
 * com os olhos na lâmpada de fenda, não na tela. Curtos, baixos e com timbre
 * diferente do "paciente chamado" — subindo = "pode falar", descendo =
 * "parou de ouvir".
 */
export function playVoiceTone(kind: 'ouvi' | 'pronto' | 'encerrado'): void {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  try {
    const now = ctx.currentTime;
    // C5/E5; "ouvi" (Jarvis reconhecido) é uma nota só, mais aguda.
    const freqs = kind === 'ouvi' ? [880] : kind === 'pronto' ? [523.25, 659.25] : [659.25, 523.25];
    freqs.forEach((freq, i) => {
      const start = i * 0.1;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.2, now + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + 0.09);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + 0.12);
    });
  } catch {
    // Áudio indisponível/bloqueado — silencioso, não é fatal.
  }
}
